import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { ToastProvider } from "@/components/ui/Toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { LoginPage } from "@/pages/LoginPage";
import { SignupPage } from "@/pages/SignupPage";
import { OnboardingWizard } from "@/pages/OnboardingWizard";
import { DashboardHome } from "@/pages/DashboardHome";
import { InboxPage } from "@/pages/InboxPage";
import { AgentDetailPage } from "@/pages/AgentDetailPage";
import { AgentBuilderPage } from "@/pages/AgentBuilderPage";
import { ChannelsPage } from "@/pages/ChannelsPage";
import { UsageBillingPage } from "@/pages/UsageBillingPage";
import { SandboxPage } from "@/pages/SandboxPage";
import { SessionsPage } from "@/pages/SessionsPage";
import { useAuthStore } from "@/stores/useAuthStore";

/* ------------------------------------------------------------------ */
/*  Auth-protected route wrapper                                       */
/* ------------------------------------------------------------------ */

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, loadSession } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/* ------------------------------------------------------------------ */
/*  Placeholder pages — will be replaced in later batches              */
/* ------------------------------------------------------------------ */

function TeamPage() {
  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Team</h1>
      <p className="text-muted-foreground">
        Team management placeholder — Phase 4 Batch 4
      </p>
    </div>
  );
}

function NotificationsPage() {
  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Notifications</h1>
      <p className="text-muted-foreground">
        Notifications placeholder — Phase 4 Batch 4
      </p>
    </div>
  );
}

function SettingsPage() {
  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Settings</h1>
      <p className="text-muted-foreground">
        Settings page placeholder — Phase 4 Batch 4
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  App Router                                                         */
/* ------------------------------------------------------------------ */

export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />

          {/* Dashboard routes — nested under layout, auth-protected, error-bounded */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <DashboardLayout />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="agents/new" element={<AgentBuilderPage />} />
            <Route path="agents/:agentId" element={<AgentDetailPage />} />
            <Route path="channels" element={<ChannelsPage />} />
            <Route path="sandbox" element={<SandboxPage />} />
            <Route path="usage" element={<UsageBillingPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Redirect root to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
